import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {categoriesApi} from '../api';
import {CategoriesFilter, CategoryWithMetaModel, CreateCategoryDTO} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type CategoriesState = {
  getInfiniteCategories: UseInfiniteQueryResult<InfiniteData<CategoryWithMetaModel>, Error>;
  filters: CategoriesFilter;
  updateFilter: (newFilter: Partial<CategoriesFilter>) => void;
  createCategoryMutation: UseMutationResult<unknown, Error, CreateCategoryDTO>;
};

const DEFAULT_PAGE_SIZE = 25;

interface UseCategoriesOptions {
  enableInfiniteQuery?: boolean;
}

export const useCategories = (
  initialFilter: CategoriesFilter = {},
  {enableInfiniteQuery = true}: UseCategoriesOptions = {},
): CategoriesState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<CategoriesFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteCategories = useInfiniteQuery<
    CategoryWithMetaModel,
    Error,
    InfiniteData<CategoryWithMetaModel>,
    ['categories', 'infinite', Omit<CategoriesFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['categories', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return categoriesApi.getCategories({...infiniteQueryFilters, pageSize: filters.pageSize, after: pageParam});
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
    enabled: enableInfiniteQuery,
  });

  const updateFilter = useCallback((newFilter: Partial<CategoriesFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  const createCategoryMutation = useMutation({
    mutationFn: categoriesApi.createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['categories', 'infinite'], refetchType: 'all'});
      queryClient.invalidateQueries({queryKey: ['categoryGroups']});
      createNotification({
        title: 'Категория успешно создана',
        name: 'createCategory',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteCategories,
    filters,
    updateFilter,
    createCategoryMutation,
  };
};
